import React from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Typography,
  Chip
} from '@mui/material';
import {
  Search,
  Clear,
  Refresh,
  FilterList,
  ChevronLeft,
  ChevronRight
} from '@mui/icons-material';
import { MAIL_COLORS } from '../constants';
import { MailFolderKey } from '../types';

interface MailToolbarProps {
  currentFolderLabel: string;
  totalMessagesCount: number;
  totalCount: number | null;
  page: number;
  pageSize: number;
  hasMore: boolean;
  searchQuery: string;
  filterUnreadOnly: boolean;
  isLoading: boolean;
  onSearchChange: (val: string) => void;
  onToggleUnreadOnly: () => void;
  onRefresh: () => void;
  onPrevPage: () => void;
  onNextPage: () => void;
}

export const MailToolbar: React.FC<MailToolbarProps> = ({
  currentFolderLabel,
  totalMessagesCount,
  totalCount,
  page,
  pageSize,
  hasMore,
  searchQuery,
  filterUnreadOnly,
  isLoading,
  onSearchChange,
  onToggleUnreadOnly,
  onRefresh,
  onPrevPage,
  onNextPage
}) => {
  const startItem = totalMessagesCount > 0 ? page * pageSize + 1 : 0;
  const endItem = totalCount != null
    ? Math.min(page * pageSize + totalMessagesCount, totalCount)
    : page * pageSize + totalMessagesCount;
  return (
    <Box
      sx={{
        p: 2,
        px: { xs: 2, sm: 3 },
        borderBottom: `1px solid ${MAIL_COLORS.borderLight}`,
        bgcolor: '#ffffff',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2
      }}
    >
      {/* Название папки и счетчик */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: MAIL_COLORS.textPrimary,
            fontSize: '1.1rem'
          }}
        >
          {currentFolderLabel}
        </Typography>
        <Chip
          label={`${totalMessagesCount} писем`}
          size="small"
          sx={{
            bgcolor: '#f1f5f9',
            color: '#475569',
            fontWeight: 600,
            fontSize: '0.75rem',
            height: 24
          }}
        />
      </Box>

      {/* Поиск и управляющие кнопки */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexGrow: { xs: 1, sm: 0 } }}>
        <TextField
          size="small"
          placeholder="Поиск в письмах..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          sx={{
            width: { xs: '100%', sm: 260 },
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              bgcolor: '#f8fafc',
              fontSize: '0.88rem',
              '& fieldset': { borderColor: '#e2e8f0' },
              '&:hover fieldset': { borderColor: '#cbd5e1' },
              '&.Mui-focused fieldset': { borderColor: MAIL_COLORS.primary }
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: '#94a3b8', fontSize: 19 }} />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => onSearchChange('')}>
                  <Clear sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ) : null
          }}
        />

        {/* Фильтр: Только непрочитанные */}
        <Tooltip title={filterUnreadOnly ? 'Показать все письма' : 'Только непрочитанные'}>
          <IconButton
            size="small"
            onClick={onToggleUnreadOnly}
            sx={{
              p: 1,
              borderRadius: '10px',
              border: `1px solid ${filterUnreadOnly ? MAIL_COLORS.primary : '#e2e8f0'}`,
              bgcolor: filterUnreadOnly ? '#eff8ff' : '#ffffff',
              color: filterUnreadOnly ? MAIL_COLORS.primary : '#64748b',
              '&:hover': {
                bgcolor: filterUnreadOnly ? '#e0f2fe' : '#f8fafc'
              }
            }}
          >
            <FilterList sx={{ fontSize: 19 }} />
          </IconButton>
        </Tooltip>

        {/* Кнопка обновления */}
        <Tooltip title="Обновить письма">
          <IconButton
            size="small"
            onClick={onRefresh}
            disabled={isLoading}
            sx={{
              p: 1,
              borderRadius: '10px',
              border: '1px solid #e2e8f0',
              bgcolor: '#ffffff',
              color: '#64748b',
              '&:hover': {
                bgcolor: '#f8fafc',
                color: '#1e293b'
              }
            }}
          >
            <Refresh
              sx={{
                fontSize: 19,
                animation: isLoading ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' }
                }
              }}
            />
          </IconButton>
        </Tooltip>

        {/* Пагинация (страницы 1-50 из 1437) */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            borderLeft: `1px solid ${MAIL_COLORS.borderLight}`,
            pl: 1.5,
            ml: 0.5
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: '#475569',
              fontSize: '0.82rem',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              userSelect: 'none',
              px: 0.5
            }}
          >
            {totalCount != null
              ? `${startItem}–${endItem} из ${totalCount}`
              : hasMore
              ? `${startItem}–${endItem}+`
              : `${startItem}–${endItem}`}
          </Typography>

          <Tooltip title="Предыдущая страница">
            <span>
              <IconButton
                size="small"
                onClick={onPrevPage}
                disabled={page === 0 || isLoading}
                sx={{
                  p: 0.75,
                  borderRadius: '8px',
                  color: '#64748b',
                  '&:hover': { bgcolor: '#f1f5f9' }
                }}
              >
                <ChevronLeft sx={{ fontSize: 20 }} />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Следующая страница">
            <span>
              <IconButton
                size="small"
                onClick={onNextPage}
                disabled={!hasMore || isLoading}
                sx={{
                  p: 0.75,
                  borderRadius: '8px',
                  color: '#64748b',
                  '&:hover': { bgcolor: '#f1f5f9' }
                }}
              >
                <ChevronRight sx={{ fontSize: 20 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

export default MailToolbar;
